import React, { useState, useEffect, useCallback } from 'react';
import { Users, Briefcase } from 'lucide-react';
import { teamAPI, partnersAPI } from '../services/api';

const TeamAndPartners = ({ isAdmin }) => {
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [teamRes, partnersRes] = await Promise.all([
        teamAPI.getAll(),
        partnersAPI.getAll(),
      ]);
      setTeam(Array.isArray(teamRes.data) ? teamRes.data : []);
      setPartners(Array.isArray(partnersRes.data) ? partnersRes.data : []);
    } catch (error) {
      console.error('Error loading team/partners:', error);
      setTeam([]);
      setPartners([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const defaultTeam = [
    { id: 1, name: 'Sophie Martin', role: 'CEO & Fondatrice', avatar: 'https://ui-avatars.com/api/?name=Sophie+Martin&background=60a5fa&color=fff' },
    { id: 2, name: 'Marc Dubois', role: 'CTO', avatar: 'https://ui-avatars.com/api/?name=Marc+Dubois&background=a78bfa&color=fff' },
    { id: 3, name: 'Julie Bernard', role: 'Responsable IA', avatar: 'https://ui-avatars.com/api/?name=Julie+Bernard&background=f472b6&color=fff' },
  ];

  const defaultPartners = [
    { id: 1, name: 'French Tech Côte d\'Azur', logo: 'https://ui-avatars.com/api/?name=FrenchTech&background=0066ff&color=fff&size=200&bold=true&rounded=true' },
    { id: 2, name: 'Région Sud PACA', logo: 'https://ui-avatars.com/api/?name=Région+Sud&background=ff6b35&color=fff&size=200&bold=true&rounded=true' },
    { id: 3, name: 'BPI France', logo: 'https://ui-avatars.com/api/?name=BPI+France&background=00b894&color=fff&size=200&bold=true&rounded=true' },
  ];

  const displayTeam = team.length > 0 ? team : defaultTeam;
  const displayPartners = partners.length > 0 ? partners : defaultPartners;

  const getPartnerLogo = (p) => {
    const name = encodeURIComponent(p?.name || 'Partenaire');
    const fallback = `https://ui-avatars.com/api/?name=${name}&background=0066ff&color=fff&size=200&bold=true&rounded=true`;
    return p?.logo && typeof p.logo === 'string' && p.logo.trim().length > 0 ? p.logo : fallback;
  };

  return (
    <div className="space-y-6 animate-slideIn">
      {/* Team Section */}
      <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Notre Équipe
        </h2>
        <div className="space-y-3">
          {displayTeam.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
              data-testid={`team-member-${member.id}`}
            >
              <img
                src={member.avatar || member.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || '')}&background=60a5fa&color=fff`}
                alt={member.name}
                className="w-12 h-12 rounded-full border-2 border-blue-400"
                loading="lazy"
              />
              <div>
                <p className="text-white font-medium" data-testid="member-name">{member.name}</p>
                <p className="text-sm text-gray-400" data-testid="member-role">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Partners Section */}
      <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-blue-400" />
          Nos Partenaires
        </h2>
        <div className="space-y-3">
          {displayPartners.map((partner) => (
            <div
              key={partner.id}
              className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-700/30 to-slate-600/20 rounded-2xl hover:from-slate-700/50 hover:to-slate-600/30 transition-all hover:scale-[1.02] duration-300 border border-slate-600/30 hover:border-blue-500/40 shadow-lg hover:shadow-blue-500/20"
              data-testid={`partner-${partner.id}`}
            >
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500/20 to-purple-500/20 p-0.5 shadow-lg">
                <div className="w-full h-full rounded-2xl overflow-hidden bg-slate-800/50 backdrop-blur-sm">
                  <img
                    src={getPartnerLogo(partner)}
                    alt={partner.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const name = encodeURIComponent(partner?.name || 'Partenaire');
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${name}&background=0066ff&color=fff&size=200&bold=true&rounded=true`;
                      e.currentTarget.onerror = null;
                    }}
                  />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm mb-1" data-testid="partner-name">
                  {partner.name}
                </p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border border-blue-400/30">
                  Partenaire officiel
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamAndPartners;
